import { useMemo } from 'react';

interface SplitConfigOptions {
  businessId?: string | number;
  userId?: string | number;
  userSettings?: {
    role?: string;
    accountType?: string;
  };
}

/**
 * Default Split.io SDK configuration
 * These values are optimized for production use with dynamic configuration
 */
const DEFAULT_SPLIT_CONFIG = {
  startup: {
    readyTimeout: 10000, // Wait up to 10 seconds for initial flag synchronization
    requestTimeoutBeforeReady: 5000, // Request timeout for flag fetches
  },
  scheduler: {
    featuresRefreshRate: 30, // Check for flag updates every 30 seconds (dynamic configuration)
    segmentsRefreshRate: 60, // Check for segment updates every 60 seconds
    metricsRefreshRate: 300, // Enable metrics posting every 5 minutes
  },
  features: {
    streaming: true, // Enable streaming for real-time updates
  },
  // Proxy configuration - will be added dynamically if proxy URL is provided
  urls: {},
} as const;

/**
 * Custom hook to generate Split.io SDK configuration
 * Handles user/business targeting, testing scenarios, and environment-specific settings
 */
export const useSplitConfig = ({ businessId, userId, userSettings }: SplitConfigOptions = {}) => {
  return useMemo(() => {
    // Use businessId as the primary key, fallback to userId, then anonymous
    const key = businessId || userId || 'anonymous';
    const trafficType = businessId ? 'business' : (userId ? 'user' : 'anonymous');

    // For testing: use invalid API key if URL parameter is present (development only)
    const useInvalidKey = process.env.NODE_ENV === 'development' &&
      new URLSearchParams(window.location.search).get('invalidSplitKey') === 'true';

    const authKey = useInvalidKey
      ? 'invalid-key-for-testing-12345'
      : process.env.VITE_APP_SPLIT_AUTHORIZATION_KEY;

    if (useInvalidKey) {
      console.warn('🧪 Using INVALID Split.io API key for testing error scenarios');
    }

    // Configure proxy URLs if proxy URL is provided
    const proxyUrl = process.env.VITE_APP_SPLIT_PROXY_URL;
    if (!proxyUrl) {
      console.error('[Split] Missing VITE_APP_SPLIT_PROXY_URL — Split SDK may fail to initialize.');
    }

    const apiUrl = new URL("/api", proxyUrl).toString();
    const urls = proxyUrl ? {
      sdk: apiUrl,
      events: apiUrl,
      auth: apiUrl,
      streaming: apiUrl,
      telemetry: apiUrl
    } : {};

    if (proxyUrl) {
      console.log('🔄 Using Split.io proxy at:', proxyUrl);
    }

    return {
      core: {
        authorizationKey: authKey,
        key: String(key), // Ensure key is always a string
      },
      ...DEFAULT_SPLIT_CONFIG,
      // Override urls with proxy configuration if available
      urls: {
        ...DEFAULT_SPLIT_CONFIG.urls,
        ...urls,
      },
      trafficType,
      // debug: process.env.NODE_ENV === 'development',
      debug: false,
      // Add user attributes for better targeting
      attributes: {
        businessId: businessId || null,
        userId: userId || null,
        environment: process.env.NODE_ENV || 'development',
        // Add any other relevant business attributes
        ...(userSettings && {
          userRole: userSettings.role,
          accountType: userSettings.accountType,
        }),
      },
    };
  }, [businessId, userId, userSettings]);
};